import * as React from 'react'
import { Gauge } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Card, CardHeader, CardTitle, CardAction, CardContent } from '../core/card'
import { type PeriodValue } from '../core/period-picker'
import {
  StatBoard,
  PerformanceControls,
  type PerformanceStat,
} from './performance-charts'

/**
 * StatsWidget — the "Stats" KPI board as a standalone card. A row of `Stat`
 * tiles with a `PeriodPicker` in the header; height fits the content.
 *
 *   <StatsWidget stats={stats} onPeriodApply={reload} />
 */

export interface StatsWidgetProps {
  stats: PerformanceStat[]
  periodLabel?: React.ReactNode
  defaultPeriod?: PeriodValue
  onPeriodApply?: (value: PeriodValue) => void
  /** Wire the ⋯ menu's "Hide widget" action; hidden when omitted. */
  onHide?: () => void
  /**
   * Force the stat grid to `1` column (a vertical stack — a narrow side column),
   * `2` (a 2×2 for four stats — a square widget beside another), or `4` (one row).
   * Default: responsive (2 up → 4 up at `sm`).
   */
  statColumns?: 1 | 2 | 4
  title?: React.ReactNode
  className?: string
}

export const StatsWidget = React.forwardRef<HTMLDivElement, StatsWidgetProps>(
  (
    { stats, periodLabel = 'Period', defaultPeriod, onPeriodApply, onHide, statColumns, title = 'Stats', className },
    ref
  ) => (
    <Card ref={ref} className={cn('flex w-full max-w-2xl flex-col', className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge aria-hidden="true" className="size-4 text-fbui-gray-500" />
          {title}
        </CardTitle>
        <CardAction>
          <PerformanceControls
            periodLabel={periodLabel}
            defaultPeriod={defaultPeriod}
            onPeriodApply={onPeriodApply}
            onHide={onHide}
            hideLabel="Stats options"
          />
        </CardAction>
      </CardHeader>
      {/* A single-column stack (a tall side column) sits at the top; the square
          2×2 centers when the card is stretched to a neighbor's height. */}
      <CardContent
        className={cn(
          'flex min-h-0 flex-1 flex-col',
          statColumns === 1 ? 'justify-start' : 'justify-center'
        )}
      >
        <StatBoard stats={stats} columns={statColumns} />
      </CardContent>
    </Card>
  )
)
StatsWidget.displayName = 'StatsWidget'
